import { create } from 'zustand';

const STORAGE_KEY = 'mlm.member.autoship-cart';

/**
 * Auto-Ship cart (UI state) — a SEPARATE productId → qty map from the normal
 * shopping cart (`useCartStore`), so building a recurring order never disturbs a
 * member's in-progress checkout. Persisted to localStorage so picks survive
 * navigating between the Store and the Auto-Ship page. Cleared once the
 * subscription is created.
 */
type State = {
  items: Record<string, number>;
  add: (productId: string, qty?: number) => void;
  setQty: (productId: string, qty: number) => void;
  clear: () => void;
};

function readStored(): Record<string, number> {
  if (typeof window === 'undefined') return {};
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '{}');
  } catch {
    return {};
  }
}

function writeStored(items: Record<string, number>) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* ignore quota / private-mode */
  }
}

export const useAutoshipCartStore = create<State>((set) => ({
  items: readStored(),
  add: (productId, qty = 1) =>
    set((s) => {
      const items = { ...s.items, [productId]: (s.items[productId] ?? 0) + qty };
      writeStored(items);
      return { items };
    }),
  setQty: (productId, qty) =>
    set((s) => {
      const items = { ...s.items };
      if (qty <= 0) delete items[productId];
      else items[productId] = qty;
      writeStored(items);
      return { items };
    }),
  clear: () => {
    writeStored({});
    return set({ items: {} });
  },
}));
